import React, { useState } from "react";
import { useAuth } from "./AuthContext";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, APIdomain } = useAuth();

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f9fafb",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "1rem",
    },
    card: {
      maxWidth: "28rem",
      width: "100%",
      backgroundColor: "white",
      padding: "2rem",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      marginBottom: "1rem",
    },
    title: {
      fontSize: "1.875rem",
      fontWeight: "bold",
      color: "#111827",
      textAlign: "center",
      marginBottom: "0.5rem",
    },
    subtitle: {
      color: "#6b7280",
      textAlign: "center",
      marginBottom: "2rem",
    },
    inputGroup: {
      marginBottom: "1.5rem",
    },
    passwordContainer: {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    label: {
      display: "block",
      fontSize: "0.875rem",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "0.5rem",
    },
    input: {
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.375rem",
      fontSize: "1rem",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    inputFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
    passwordInput: {
      width: "100%",
      padding: "0.75rem",
      paddingRight: "3rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.375rem",
      fontSize: "1rem",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    eyeIcon: {
      position: "absolute",
      right: "0.75rem",
      cursor: "pointer",
      color: "#6b7280",
      fontSize: "1.25rem",
      userSelect: "none",
      padding: "0.25rem",
    },
    button: {
      width: "100%",
      padding: "0.75rem 1rem",
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "1rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    buttonHover: {
      backgroundColor: "#2563eb",
    },
    buttonDisabled: {
      backgroundColor: "#9ca3af",
      cursor: "not-allowed",
    },
    toggleButton: {
      color: "#3b82f6",
      backgroundColor: "transparent",
      border: "none",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      textDecoration: "underline",
    },
    message: {
      textAlign: "center",
      fontSize: "0.875rem",
      marginTop: "1rem",
    },
    successMessage: {
      color: "#059669",
    },
    errorMessage: {
      color: "#dc2626",
    },
    userList: {
      marginTop: "1rem",
      padding: "1rem",
      backgroundColor: "#f9fafb",
      borderRadius: "0.375rem",
      border: "1px solid #e5e7eb",
    },
    userItem: {
      padding: "0.75rem",
      backgroundColor: "white",
      borderRadius: "0.375rem",
      marginBottom: "0.5rem",
      border: "1px solid #e5e7eb",
    },
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setMessage("");

    try {
      const endpoint = isLogin ? "/api/users/login" : "/api/users/create";
      const payload = isLogin
        ? { username: formData.username, password: formData.password }
        : {
            username: formData.username,
            email: formData.email,
            password: formData.password,
          };

      const response = await fetch(`${APIdomain}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setFormData({ username: "", email: "", password: "" });

        if (data.token) {
          login({
            id: data.user.id,
            username: data.user.username,
            email: data.user.email,
            purchases: data.user.purchases,
            type: data.user.type,
            token: data.token,
          });
        }
      } else {
        setMessage(data.error || "An error occurred");
      }
    } catch (error) {
      setMessage("Network error. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${APIdomain}/api/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Password reset email sent successfully");
        setResetEmail("");
        setShowForgotPassword(false);
      } else {
        setMessage(data.error || "An error occurred");
      }
    } catch (error) {
      setMessage("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({ username: "", email: "", password: "" });
    setMessage("");
    setShowForgotPassword(false);
    setShowPassword(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div>
          <h2 style={styles.title}>
            {showForgotPassword
              ? "Reset Password"
              : isLogin
              ? "Sign In"
              : "Sign Up"}
          </h2>
          <p style={styles.subtitle}>
            {showForgotPassword
              ? "Enter your email to reset password"
              : isLogin
              ? "Welcome back!"
              : "Create your account"}
          </p>
        </div>

        {showForgotPassword ? (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                style={styles.input}
                placeholder="Enter your email"
              />
            </div>

            <button
              onClick={handleForgotPassword}
              disabled={loading}
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Sending..." : "Send Reset Email"}
            </button>

            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <button
                onClick={() => setShowForgotPassword(false)}
                style={styles.toggleButton}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                style={styles.input}
                placeholder="Enter your username"
              />
            </div>

            {!isLogin && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  placeholder="Enter your email"
                />
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength="6"
                  style={styles.passwordInput}
                  placeholder="Enter your password"
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeIcon}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "👁️" : "🙈"}
                </span>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Processing..." : isLogin ? "Sign In" : "Sign Up"}
            </button>

            {isLogin && (
              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <button
                  onClick={() => setShowForgotPassword(true)}
                  style={styles.toggleButton}
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <button onClick={toggleMode} style={styles.toggleButton}>
                {isLogin
                  ? "Don't have an account? Sign up"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        )}

        {message && (
          <div
            style={{
              ...styles.message,
              ...(message.includes("successful") || message.includes("created")
                ? styles.successMessage
                : styles.errorMessage),
            }}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;
